import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { Upload, X, BookOpen, FileText, Image } from "lucide-react";
import { EBook } from "@brovicms/models";
import ebookService from "../../services/ebookService";
import ImagePreviewModal from "../audio/ImagePreviewModal";
import PDFViewModal from "../audio/PDFViewModal";
import { EBOOK_CATEGORIES } from "../../constants/categories";

// navigation prompt removed

interface EBookFormData {
  draftId: string;
  title: string;
  description: string;
  author: string;
  category: {
    id: string;
    name: string;
    slug: string;
    isActive: boolean;
    sortOrder: number;
  };
  tags: string[];
  language: string;
  isPublished: boolean;
  isFeatured: boolean;
  isPremium: boolean;
  coverImageFile?: File | null;
  pdfFile?: File | null;
  indexImages: (File | null)[];
  coverImage: {
    url: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    width: number;
    height: number;
  } | null;
  file: {
    url: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    pages: number;
  } | null;
}

interface EBookFormProps {
  ebook?: EBook | null;
  onSubmit: (success: boolean) => void;
  onCancel: () => void;
  loading: boolean;
  readOnly?: boolean;
}

const EBookForm: React.FC<EBookFormProps> = ({
  ebook,
  onSubmit,
  onCancel,
  loading,
  readOnly = false,
}) => {
  const [draftId, setDraftId] = useState<string | null>(null);
  const [isAnythingChanged, setIsAnythingChanged] = useState(false);
  const isDraftCreatedRef = useRef<boolean>(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [uploadedPdfUrl, setUploadedPdfUrl] = useState<string>("");
  const [indexUploading, setIndexUploading] = useState<boolean[]>([]);
  const [coverImageUrl, setCoverImageUrl] = useState<string>("");
  const [indexPagesImages, setIndexPagesImages] = useState<
    { url: string; alt: string; width: number; height: number }[]
  >([]);
  const [previewUrls, setPreviewUrls] = useState<(string | null)[]>([]);
  const [showIndexPreviewModal, setShowIndexPreviewModal] = useState(false);
  const [indexPreviewFile, setIndexPreviewFile] = useState<File | null>(null);
  const [indexPreviewUrl, setIndexPreviewUrl] = useState<string>("");
  const [indexPreviewIndex, setIndexPreviewIndex] = useState<number | null>(
    null
  );
  const [showIndexViewModal, setShowIndexViewModal] = useState(false);
  const [indexViewUrl, setIndexViewUrl] = useState<string>("");
  const [indexViewIndex, setIndexViewIndex] = useState<number | null>(null);
  const [showCoverPreviewModal, setShowCoverPreviewModal] = useState(false);
  const [coverPreviewFile, setCoverPreviewFile] = useState<File | null>(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string>("");
  const [showPdfPreviewModal, setShowPdfPreviewModal] = useState(false);
  const [pdfPreviewFile, setPdfPreviewFile] = useState<File | null>(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitted },
    watch,
    setValue,
    setError,
    clearErrors,
    reset,
  } = useForm<EBookFormData>({
    defaultValues: {
      title: ebook?.title || "",
      description: ebook?.description || "",
      author: ebook?.author || "",
      category: ebook?.category || {
        id: "",
        name: "",
        slug: "",
        isActive: true,
        sortOrder: 0,
      },
      tags: ebook?.tags || [],
      language: ebook?.language || "en",
      isPublished: ebook?.isPublished || true,
      isFeatured: ebook?.isFeatured || false,
      isPremium: ebook?.isPremium || false,
      coverImageFile: null,
      pdfFile: null,
      indexImages: [null],
      coverImage: null,
      file: null,
      // Initialize with one empty slot
    },
  });

  // tags management: watch tags array from form and provide a local input for adding tags
  const watchedTags = (watch("tags") as string[]) || [];
  const [tagInput, setTagInput] = useState<string>('');

  const addTag = (value?: string) => {
  const raw = ((value ?? tagInput) || '').trim();
    if (!raw) return;
    const current = Array.isArray(watchedTags) ? watchedTags : [];
    if (!current.includes(raw)) {
      try {
        setValue('tags' as any, [...current, raw]);
      } catch (e) {
        /* ignore */
      }
    }
    setTagInput('');
  };

  const removeTag = (index: number) => {
    const current = Array.isArray(watchedTags) ? watchedTags : [];
    const next = current.filter((_, i) => i !== index);
    try {
      setValue('tags' as any, next);
    } catch (e) {
      /* ignore */
    }
  };

  const handleTagKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
    if (e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  // We'll create drafts on-demand via waitForDraftId to avoid duplicate creations.
  // (removed effect that created drafts from isAnythingChanged to prevent races)

  // Initialize form when editing an existing ebook
  useEffect(() => {
    if (ebook) {
      // populate basic fields
      setDraftId(ebook.id || null);
      isDraftCreatedRef.current = true;
      const formValues: Partial<EBookFormData> = {
        draftId: ebook.id || "",
        title: ebook.title || "",
        description: ebook.description || "",
        author: ebook.author || "",
        category: ebook.category || {
          id: "",
          name: "",
          slug: "",
          isActive: true,
          sortOrder: 0,
        },
        tags: ebook.tags || [],
        language: ebook.language || "en",
        isPublished: ebook.isPublished || false,
        isFeatured: ebook.isFeatured || false,
        isPremium: ebook.isPremium || false,
        coverImage: ebook.coverImage
          ? {
              url: ebook.coverImage.url || "",
              fileName:
                (ebook.coverImage as any).fileName ||
                (ebook.coverImage as any).alt ||
                "",
              fileSize: (ebook.coverImage as any).fileSize || 0,
              mimeType: (ebook.coverImage as any).mimeType || "",
              width: (ebook.coverImage as any).width || 0,
              height: (ebook.coverImage as any).height || 0,
            }
          : null,
        file: ebook.file
          ? {
              url:
                (ebook.file as any).url || (ebook.file as any).fileName || "",
              fileName: (ebook.file as any).fileName || "",
              fileSize: (ebook.file as any).fileSize || 0,
              mimeType: (ebook.file as any).mimeType || "application/pdf",
              pages: (ebook.file as any).pages || 0,
            }
          : null,
        indexImages: (ebook.indexPagesImages || []).length
          ? (ebook.indexPagesImages || []).map(() => null)
          : [null],
      };

      // reset the whole form so react-hook-form updates defaultValues and registered fields
      try {
        (reset as any) && reset(formValues as any);
      } catch (e) {
        /* ignore */
      }

      // cover/pdf/preview state
      setCoverImageUrl(ebook.coverImage?.url || "");
      setUploadedPdfUrl((ebook.file as any)?.url || "");

      const existingIndexUrls = (ebook.indexPagesImages || []).map(
        (p: any) => p.url
      );
      setPreviewUrls(existingIndexUrls.length ? existingIndexUrls : []);
      setIndexPagesImages(ebook.indexPagesImages || []);
      setIndexUploading(
        existingIndexUrls.length ? existingIndexUrls.map(() => false) : []
      );
    }
  }, [ebook]);

  // Do NOT auto-delete storage or draft on unmount; keep structure intact
  // Cleanup will be explicit on Cancel only

  // watch for any form change and mark the form as dirty -> create draft only then
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (!isAnythingChanged) setIsAnythingChanged(true);
    });
    return () => subscription.unsubscribe();
  }, [watch, isAnythingChanged]);

  // Helper to wait for draftId to be created (used by upload handlers)
  const draftCreationPromiseRef = useRef<Promise<string | null> | null>(null);

  const waitForDraftId = async (timeout = 5000) => {
    // If a draft already exists, return it
    if (draftId) return draftId;

    // If draft creation is already in progress, await it
    if (draftCreationPromiseRef.current) {
      try {
        const id = await draftCreationPromiseRef.current;
        return id;
      } catch (e) {
        // fall through to try creating again
      }
    }

    // Start creating draft if not already
    if (!isDraftCreatedRef.current) {
      const p = (async () => {
        try {
          const result = await ebookService.createDraft();
          const newId = result.data?.docId || "";
          setDraftId(newId);
          try {
            setValue("draftId", newId);
          } catch (e) {
            /* ignore */
          }
          isDraftCreatedRef.current = true;
          return newId;
        } catch (err) {
          console.error("Failed to create draft (waitForDraftId):", err);
          return null;
        }
      })();
      draftCreationPromiseRef.current = p;
      const id = await p;
      draftCreationPromiseRef.current = null;
      return id;
    }

    // fallback: wait a short while for the draftId to appear
    const start = Date.now();
    while (!draftId && Date.now() - start < timeout) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 100));
    }
    return draftId;
  };

  // Re-add navigation and discard confirmation when there are unsaved changes
  useEffect(() => {
    if (!isAnythingChanged || !draftId || !!ebook) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
      return '';
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    let suppressPop = false;

    const onPopState = (e: PopStateEvent) => {
      if (suppressPop) {
        suppressPop = false;
        return;
      }
      if (!isAnythingChanged || !draftId) return;
      const confirmed = window.confirm(
        "Are you sure you want to discard this draft? All data and uploads will be deleted."
      );
      if (confirmed) {
        try {
          ebookService.deleteDraft(draftId);
        } catch (err) {
          /* ignore */
        }
        // allow navigation
      } else {
        // user cancelled -> move forward in history to stay on page
        suppressPop = true;
        try {
          window.history.go(1);
        } catch (err) {
          // ignore
        }
      }
    };

    const onDocumentClick = (ev: MouseEvent) => {
      if (!ev || !ev.target) return;
      const a = (ev.target as HTMLElement).closest("a") as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.getAttribute("href");
      if (!href || (href.startsWith("http") && !href.startsWith(window.location.origin))) return;
      // internal link
      if (href.startsWith("#")) return;
      // prevent navigation and show confirm
      ev.preventDefault();
      const confirmed = window.confirm(
        "Are you sure you want to discard this draft? All data and uploads will be deleted."
      );
      if (confirmed) {
        try {
          ebookService.deleteDraft(draftId);
        } catch (err) {
          /* ignore */
        }
        // navigate to href
        window.location.href = href!;
      }
      // else do nothing (stay)
    };

    window.addEventListener("popstate", onPopState);
    document.addEventListener("click", onDocumentClick, true);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", onPopState);
      document.removeEventListener("click", onDocumentClick, true);
    };
  }, [isAnythingChanged, draftId, ebook]);

  const handleCoverUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        e.target.value = '';
        return;
      }

      // Validate image dimensions
      const img = document.createElement('img');
      img.onload = () => {
        const width = img.width;
        const height = img.height;
        const aspectRatio = width / height;
        const targetAspectRatio = 2 / 3; // Standard ebook cover aspect ratio (2:3)
        const tolerance = 0.05; // 5% tolerance

        // // Check aspect ratio (should be approximately 2:3)
        // if (Math.abs(aspectRatio - targetAspectRatio) > tolerance) {
        //   alert(
        //     `Cover image must have a 2:3 aspect ratio (width:height).\n` +
        //     `Current dimensions: ${width}x${height} (ratio: ${aspectRatio.toFixed(2)})\n` +
        //     `Recommended sizes: 1200x1800px or 1600x2560px`
        //   );
        //   e.target.value = '';
        //   return;
        // }

        // // Check minimum dimensions
        // if (width < 600 || height < 900) {
        //   alert(
        //     `Cover image is too small. Minimum dimensions: 600x900px\n` +
        //     `Current dimensions: ${width}x${height}px\n` +
        //     `Recommended sizes: 1200x1800px or 1600x2560px`
        //   );
        //   e.target.value = '';
        //   return;
        // }

        // // Check maximum dimensions (optional, but warn if too large)
        // if (width > 3000 || height > 4500) {
        //   const proceed = window.confirm(
        //     `Cover image is very large (${width}x${height}px). This may affect performance.\n` +
        //     `Recommended maximum: 1600x2560px\n\n` +
        //     `Do you want to proceed anyway?`
        //   );
        //   if (!proceed) {
        //     e.target.value = '';
        //     return;
        //   }
        // }

        const url = URL.createObjectURL(file);
        setCoverPreviewFile(file);
        setCoverPreviewUrl(url);
        setShowCoverPreviewModal(true);
      };
      img.onerror = () => {
        alert('Failed to load image. Please select a valid image file.');
        e.target.value = '';
      };
      img.src = URL.createObjectURL(file);
    }
  };

  const confirmCoverPreviewUpload = async () => {
    const file = coverPreviewFile;
    if (file) {
      // Get actual image dimensions
      const img = document.createElement('img');
      const dimensionsPromise = new Promise<{ width: number; height: number }>((resolve, reject) => {
        img.onload = () => {
          resolve({ width: img.width, height: img.height });
        };
        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };
        img.src = URL.createObjectURL(file);
      });

      let width = 0;
      let height = 0;
      try {
        const dimensions = await dimensionsPromise;
        width = dimensions.width;
        height = dimensions.height;
      } catch (error) {
        console.error('Failed to get image dimensions:', error);
      }

      if (!isAnythingChanged) setIsAnythingChanged(true);

      const id = draftId || (await waitForDraftId());
      if (!id) {
        console.error("No draft id available for cover upload");
        return;
      }
      setCoverUploading(true);
      try {
        // Upload to Firebase Storage
        const downloadURL = await ebookService.uploadFile(id, file, "cover");

        // Update draft with cover URL
        await ebookService.updateDraft(id, { coverUrl: downloadURL });

        // Store the URL for later use
        setCoverImageUrl(downloadURL);

        setValue("coverImage", {
          url: downloadURL,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          width: width,
          height: height,
        });
      } catch (error) {
        console.error("Failed to upload cover:", error);
      } finally {
        setCoverUploading(false);
        setShowCoverPreviewModal(false);
        setCoverPreviewFile(null);
        if (coverPreviewUrl) {
          try {
            URL.revokeObjectURL(coverPreviewUrl);
          } catch (e) {
            /* ignore */
          }
        }
        setCoverPreviewUrl("");
      }
    }
  };

  const cancelCoverPreviewUpload = () => {
    setShowCoverPreviewModal(false);
    setCoverPreviewFile(null);
    if (coverPreviewUrl) {
      try {
        URL.revokeObjectURL(coverPreviewUrl);
      } catch (e) {
        /* ignore */
      }
      setCoverPreviewUrl("");
    }
    // reset the file input so selecting same file again triggers change
    const fileInput = document.getElementById("cover-upload") as HTMLInputElement | null;
    if (fileInput) fileInput.value = "";
  };

  const handlePdfUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setPdfPreviewFile(file);
      setPdfPreviewUrl(url);
      setShowPdfPreviewModal(true);
    }
  };

  const confirmPdfPreviewUpload = async () => {
    const file = pdfPreviewFile;
    if (file) {
      if (!isAnythingChanged) setIsAnythingChanged(true);

      const id = draftId || (await waitForDraftId());
      if (!id) {
        console.error("No draft id available for pdf upload");
        return;
      }
      setPdfUploading(true);
      try {
        // Upload to Firebase Storage
        const downloadURL = await ebookService.uploadFile(id, file, "ebook");

        // Update draft with PDF URL
        await ebookService.updateDraft(id, { ebookFileUrl: downloadURL });

        // Store the URL for later use
        setUploadedPdfUrl(downloadURL);

        // Update local state
        setValue("file", {
          url: downloadURL,
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          pages: 0,
        });
        // clear any manual file errors
        try {
          clearErrors("file" as any);
        } catch (e) {
          /* ignore */
        }
      } catch (error) {
        console.error("Failed to upload PDF:", error);
      } finally {
        setPdfUploading(false);
        setShowPdfPreviewModal(false);
        setPdfPreviewFile(null);
        if (pdfPreviewUrl) {
          try {
            URL.revokeObjectURL(pdfPreviewUrl);
          } catch (e) {
            /* ignore */
          }
        }
        setPdfPreviewUrl("");
      }
    }
  };

  const cancelPdfPreviewUpload = () => {
    setShowPdfPreviewModal(false);
    setPdfPreviewFile(null);
    if (pdfPreviewUrl) {
      try {
        URL.revokeObjectURL(pdfPreviewUrl);
      } catch (e) {
        /* ignore */
      }
      setPdfPreviewUrl("");
    }
    // reset the file input so selecting same file again triggers change
    const fileInput = document.getElementById("pdf-upload") as HTMLInputElement | null;
    if (fileInput) fileInput.value = "";
  };
  const handleIndexImageSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        e.target.value = '';
        return;
      }

      // Validate image dimensions for index pages
      const img = document.createElement('img');
      img.onload = () => {
        const width = img.width;
        const height = img.height;
        const aspectRatio = width / height;
        
        // Index pages should be in portrait orientation (height > width)
        // Common aspect ratios: 8.5:11 (US Letter), A4 (1:1.414), or similar
        // We'll be flexible but prefer portrait orientation
        if (aspectRatio > 1) {
          const proceed = window.confirm(
            `Index image appears to be in landscape orientation (${width}x${height}px).\n` +
            `Index pages are typically in portrait orientation.\n\n` +
            `Do you want to proceed anyway?`
          );
          if (!proceed) {
            e.target.value = '';
            return;
          }
        }

        // Check minimum dimensions
        if (width < 400 || height < 500) {
          alert(
            `Index image is too small. Minimum recommended dimensions: 400x500px\n` +
            `Current dimensions: ${width}x${height}px`
          );
          e.target.value = '';
          return;
        }

        const url = URL.createObjectURL(file);
        setIndexPreviewFile(file);
        setIndexPreviewUrl(url);
        setIndexPreviewIndex(index);
        setShowIndexPreviewModal(true);
      };
      img.onerror = () => {
        alert('Failed to load image. Please select a valid image file.');
        e.target.value = '';
      };
      img.src = URL.createObjectURL(file);
    }
  };

  const confirmIndexPreviewUpload = async () => {
    const index = indexPreviewIndex;
    const file = indexPreviewFile;
    if (file && typeof index === "number") {
      // Get actual image dimensions
      const img = document.createElement('img');
      const dimensionsPromise = new Promise<{ width: number; height: number }>((resolve, reject) => {
        img.onload = () => {
          resolve({ width: img.width, height: img.height });
        };
        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };
        img.src = URL.createObjectURL(file);
      });

      let width = 0;
      let height = 0;
      try {
        const dimensions = await dimensionsPromise;
        width = dimensions.width;
        height = dimensions.height;
      } catch (error) {
        console.error('Failed to get image dimensions:', error);
      }

      if (!isAnythingChanged) setIsAnythingChanged(true);
      const id = draftId || (await waitForDraftId());
      if (!id) {
        console.error("No draft id available for index upload");
        return;
      }
      setIndexUploading((prev) => {
        const next = [...(prev || [])];
        next[index] = true;
        return next;
      });
      try {
        const downloadURL = await ebookService.uploadFile(id, file, "index", index);
        const currentIndexImages = watch("indexImages") || [];
        const newIndexImages = [...currentIndexImages];
        newIndexImages[index] = file;
        setValue("indexImages", newIndexImages);
        // clear any manual indexImages errors
        try {
          clearErrors("indexImages" as any);
        } catch (e) {
          /* ignore */
        }

        // replace local preview with uploaded URL
        setPreviewUrls((prev) => {
          const next = [...(prev || [])];
          // revoke previous blob URL if it was a local preview
          const prevUrl = next[index];
          if (prevUrl && prevUrl.startsWith("blob:")) {
            try {
              URL.revokeObjectURL(prevUrl);
            } catch (e) {
              /* ignore */
            }
          }
          next[index] = downloadURL;
          return next;
        });

        const newIndexPagesImages = [...indexPagesImages];
        newIndexPagesImages[index] = {
          url: downloadURL,
          alt: `Index ${index + 1}`,
          width: width,
          height: height,
        };
        setIndexPagesImages(newIndexPagesImages);

        await ebookService.updateDraft(id, { [`indexImageUrl_${index}`]: downloadURL });
      } catch (err) {
        console.error("Failed to upload index image:", err);
      } finally {
        setIndexUploading((prev) => {
          const next = [...(prev || [])];
          next[index] = false;
          return next;
        });
        setShowIndexPreviewModal(false);
        setIndexPreviewFile(null);
        if (indexPreviewUrl) {
          try {
            URL.revokeObjectURL(indexPreviewUrl);
          } catch (e) {
            /* ignore */
          }
        }
        setIndexPreviewUrl("");
        setIndexPreviewIndex(null);
      }
    }
  };

  const cancelIndexPreviewUpload = () => {
    setShowIndexPreviewModal(false);
    setIndexPreviewFile(null);
    if (indexPreviewUrl) {
      try {
        URL.revokeObjectURL(indexPreviewUrl);
      } catch (e) {
        /* ignore */
      }
      setIndexPreviewUrl("");
    }
    setIndexPreviewIndex(null);
    // reset the file input so selecting same file again triggers change
    const fileInput = document.getElementById(
      `index-upload-${indexPreviewIndex}`
    ) as HTMLInputElement | null;
    if (fileInput) fileInput.value = "";
  };

  const removeIndexImage = (index: number) => {
    const newIndexImages = [...watch("indexImages")];
    // revoke preview URL if present
    setPreviewUrls((prev) => {
      const next = [...(prev || [])];
      const url = next[index];
      if (url && url.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          /* ignore */
        }
      }
      next[index] = null;
      return next;
    });
    newIndexImages[index] = null;
    setValue("indexImages", newIndexImages);
  };

  const addIndexImageSlot = () => {
    const currentImages = watch("indexImages") || [];
    if (currentImages.length < 4) {
      setValue("indexImages", [...currentImages, null]);
      setPreviewUrls((prev) => [...(prev || []), null]);
      setIndexUploading((prev) => [...(prev || []), false]);
    }
  };

  const removeCover = () => {
    setValue("coverImageFile", null);
    setValue("coverImage", null);
    setCoverImageUrl("");
  };

  const removePdf = () => {
    setValue("pdfFile", null);
    setValue("file", null);
    setUploadedPdfUrl("");
  };

  const onSubmitWithValidation = async (data: EBookFormData) => {
    console.log("called onSubmitWithValidation", data);
    // Validate index images (show errors under inputs)
    const indexImages = data.indexImages || [];
    const previews = previewUrls || [];
    // Count unique slots that have either a selected file or an existing preview URL.
    // This avoids double-counting the same slot when a file has been uploaded
    // and a preview URL exists for the same index.
    const maxSlots = Math.max(indexImages.length, previews.length);
    let totalIndexCount = 0;
    for (let i = 0; i < maxSlots; i++) {
      const hasFile = !!indexImages[i];
      const hasPreview = !!previews[i];
      if (hasFile || hasPreview) totalIndexCount++;
    }

    if (totalIndexCount < 1) {
      try {
        setError("indexImages" as any, {
          type: "manual",
          message: "At least 1 index image is required",
        });
      } catch (e) {
        /* ignore */
      }
      return;
    }
    if (totalIndexCount > 4) {
      try {
        setError("indexImages" as any, {
          type: "manual",
          message: "Maximum 4 index images allowed",
        });
      } catch (e) {
        /* ignore */
      }
      return;
    }

    if (draftId) {
      // Ensure PDF is uploaded
      if (!uploadedPdfUrl && !data.file?.url) {
        try {
          setError("file" as any, {
            type: "manual",
            message: "PDF file is required",
          });
        } catch (e) {
          /* ignore */
        }
        return;
      }
      try {
        const ebookData = {
          id: draftId,
          type: "ebook",
          category: {
            id: "",
            name: data.category.name,
            slug: "",
            isActive: true,
            sortOrder: 0,
          },
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: "admin",
          updatedBy: "admin",
          metadata: {
            publishing: {
              publisher: "Admin",
              publishedDate: new Date(),
              pageCount: 0,
              price: 0,
            },
          },
          stats: {
            views: 0,
            downloads: 0,
            shares: 0,
            likes: 0,
            bookmarks: 0,
            averageRating: 0,
            totalRatings: 0,
            comments: 0,
          },
          title: data.title,
          description: data.description,
          author: data.author,
          file: {
            url: data.file?.url || "",
            fileName: data.file?.fileName || "",
            fileSize: data.file?.fileSize || 0,
            mimeType: data.file?.mimeType || "",
            pages: data.file?.pages || 0,
          },
          coverImage: {
            url: data.coverImage?.url || "",
            alt: data.coverImage?.fileName || "",
            width: data.coverImage?.width || 0,
            height: data.coverImage?.height || 0,
          },
          // Use hosted Firebase URLs for indexPagesImages
          indexPagesImages: (previewUrls || [])
            .map((url, idx) => {
              if (!url) return null;
              return {
                url,
                alt: data.indexImages?.[idx]?.name || `Index ${idx + 1}`,
                width: data.indexImages?.[idx]?.size || 0,
                height: data.indexImages?.[idx]?.size || 0,
              };
            })
            .filter(Boolean),
          readingTime: Math.ceil(Number(data.file?.pages || 0) * 2),
          tableOfContents: [],
          tags: data.tags,
          language: data.language,
          isPublished: data.isPublished,
          isFeatured: data.isFeatured,
          isPremium: data.isPremium,
        };
        console.log(
          "EBookForm___onSubmitWithValidation:ebookData",
          ebookData,
          data
        );
        // Publish the draft
        const result = await ebookService.publishDraft(
          draftId,
          ebookData as EBook
        );
        console.log("EBookForm___publishDraft:result", result);
        onSubmit(true);
        return result;
      } catch (error) {
        console.error("Failed to publish draft:", error);
        throw error;
      }
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmitWithValidation)} className="space-y-6">
      {/* Basic Information */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">
            Basic Information
          </h3>
        </div>
        <div className="card-body space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                className={`input ${errors.title ? "input-error" : ""}`}
                placeholder="Enter book title"
                {...register("title", { required: "Title is required" })}
                disabled={readOnly}
              />
              {isSubmitted && errors.title && (
                <p className="mt-1 text-sm text-error-600">
                  {errors.title.message}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Author *
              </label>
              <input
                type="text"
                className={`input ${errors.author ? "input-error" : ""}`}
                placeholder="Enter author name"
                {...register("author", {
                  required: "Author is required",
                  minLength: {
                    value: 2,
                    message: "Author name must be at least 2 characters long",
                  },
                  maxLength: {
                    value: 50,
                    message: "Author name must be at most 50 characters long",
                  },
                })}
                disabled={readOnly}
              />
              {isSubmitted && errors.author && (
                <p className="mt-1 text-sm text-error-600">
                  {errors.author.message}
                </p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              rows={4}
              className={`input ${errors.description ? "input-error" : ""}`}
              placeholder="Enter book description"
              {...register("description", {
                required: "Description is required",
                minLength: {
                  value: 10,
                  message: "Description must be at least 10 characters long",
                },
              })}
              disabled={readOnly}
            />
            {isSubmitted && errors.description && (
              <p className="mt-1 text-sm text-error-600">
                {errors.description.message}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                className="input"
                value={(watch("category") as any)?.id || ""}
                onChange={(e) => {
                  const selectedCategory = EBOOK_CATEGORIES.find(cat => cat.id === e.target.value);
                  if (selectedCategory) {
                    setValue("category", {
                      id: selectedCategory.id,
                      name: selectedCategory.name,
                      slug: selectedCategory.slug,
                      isActive: selectedCategory.isActive,
                      sortOrder: selectedCategory.sortOrder,
                    });
                  }
                }}
                disabled={readOnly}
              >
                <option value="">Select category</option>
                {EBOOK_CATEGORIES.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Language
              </label>
              <select className="input" {...register("language")}>
                <option value="en">English</option>
                <option value="hi">Hindi</option>
                <option value="ta">Tamil</option>
                <option value="te">Telugu</option>
                <option value="bn">Bengali</option>
                <option value="gu">Gujarati</option>
                <option value="kn">Kannada</option>
                <option value="ml">Malayalam</option>
                <option value="mr">Marathi</option>
                <option value="or">Odia</option>
                <option value="pa">Punjabi</option>
                <option value="ur">Urdu</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tags
              </label>
              <div className="border rounded-apple p-2">
                <div className="flex flex-wrap items-center gap-2">
                  {(Array.isArray(watchedTags) ? watchedTags : []).map((t, i) => (
                    <div key={i} className="inline-flex items-center bg-gray-100 text-sm px-2 py-1 rounded-full">
                      <span className="mr-2">{t}</span>
                      {!readOnly && (
                        <button type="button" onClick={() => removeTag(i)} className="text-gray-500 hover:text-gray-700">
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  ))}

                  {!readOnly && (
                    <div className="flex items-center">
                      <input
                        type="text"
                        className="input ml-1"
                        placeholder="Type tag and press Enter"
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        onKeyDown={handleTagKeyDown}
                      />
                      <button type="button" onClick={() => addTag()} className="btn btn-sm btn-secondary ml-2">Add</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* File Uploads */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">Files</h3>
        </div>
        <div className="card-body space-y-6">
          {/* Cover Image */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <label className="block text-sm font-semibold text-gray-900">
                  Cover Image <span className="text-error-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mt-0.5">
                  2:3 aspect ratio • Recommended: 1200×1800px or 1600×2560px
                </p>
              </div>
            </div>
            {coverImageUrl ? (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="flex items-start gap-4 p-4">
                  <div className="flex-shrink-0">
                    <div className="w-32 h-48 bg-gray-50 rounded border border-gray-300 overflow-hidden flex items-center justify-center shadow-sm">
                      <img
                        src={coverImageUrl}
                        alt="Cover preview"
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    {(() => {
                      const coverImage = watch("coverImage");
                      const width = coverImage?.width;
                      const height = coverImage?.height;
                      const fileName = coverImage?.fileName;
                      
                      return (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-gray-900">Cover Image</span>
                            {width && width > 0 && height && height > 0 && (
                              <span className="text-xs text-gray-500 font-mono">
                                {width}×{height}
                              </span>
                            )}
                          </div>
                          {fileName && (
                            <p className="text-xs text-gray-500 truncate mb-3">
                              {fileName}
                            </p>
                          )}
                        </>
                      );
                    })()}
                    {!readOnly && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            const fileInput = document.getElementById("cover-upload") as HTMLInputElement | null;
                            if (fileInput) {
                              fileInput.click();
                            }
                          }}
                          className="btn btn-xs btn-secondary text-xs py-1.5"
                        >
                          <Upload className="w-3 h-3 mr-1" />
                          Replace
                        </button>
                        <button
                          type="button"
                          onClick={removeCover}
                          className="btn btn-xs btn-error text-xs py-1.5 px-2"
                          title="Remove cover image"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCoverUpload}
                  className="hidden"
                  id="cover-upload"
                />
              </div>
            ) : (
              <label
                htmlFor="cover-upload"
                className={`block border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all duration-200 ${
                  coverUploading
                    ? "border-gray-300 bg-gray-50 text-gray-400 pointer-events-none"
                    : "border-gray-300 bg-gray-50/50 text-gray-600 hover:border-primary-400 hover:bg-primary-50/30 hover:text-primary-700"
                }`}
              >
                <div className={`mb-2 transition-transform duration-200 ${coverUploading ? '' : 'group-hover:scale-110'}`}>
                  <Image className={`w-10 h-10 mx-auto ${coverUploading ? 'text-gray-400' : 'text-gray-400 group-hover:text-primary-500'}`} />
                </div>
                <p className="text-sm font-medium mb-1">
                  {coverUploading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin rounded-full h-3 w-3 border-2 border-gray-400 border-t-transparent"></span>
                      Uploading...
                    </span>
                  ) : (
                    'Upload Cover Image'
                  )}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {coverUploading ? 'Please wait...' : '2:3 aspect ratio required'}
                </p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleCoverUpload}
                  className="hidden"
                  id="cover-upload"
                  disabled={readOnly || coverUploading}
                />
              </label>
            )}
          </div>

          {/* PDF File */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              PDF File *
            </label>
            {uploadedPdfUrl ? (
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-apple border border-gray-200">
                <div className="flex items-center">
                  <FileText className="w-5 h-5 text-error-500 mr-3" />
                  <span className="text-sm font-medium text-gray-900">
                    {watch("file")?.fileName || "PDF uploaded"}
                  </span>
                </div>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={removePdf}
                    className="text-error-500 hover:text-error-700"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ) : (
              <div className="border-2 border-dashed border-gray-300 rounded-apple p-6 text-center">
                <BookOpen className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-2">Upload PDF file</p>
                {!readOnly && (
                  <>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handlePdfUpload}
                      className="hidden"
                      id="pdf-upload"
                    />
                    <label
                      htmlFor="pdf-upload"
                      className={`btn btn-secondary cursor-pointer ${
                        pdfUploading ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {pdfUploading ? "Uploading..." : "Choose PDF"}
                    </label>
                  </>
                )}
              </div>
            )}
            {isSubmitted && errors.file && (
              <p className="mt-1 text-sm text-error-600">
                {String(errors.file?.message || "PDF file is required")}
              </p>
            )}
          </div>

          {/* Index Images */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <label className="block text-sm font-semibold text-gray-900">
                  Index Images <span className="text-error-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Portrait orientation recommended • Minimum 400×500px • 1-4 images required
                </p>
              </div>
              {!readOnly && (watch("indexImages") || []).length < 4 && (
                <button
                  type="button"
                  onClick={addIndexImageSlot}
                  className="btn btn-sm btn-secondary"
                >
                  <Upload className="w-4 h-4 mr-1.5" />
                  Add Slot
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {(watch("indexImages") || []).map((image, index) => (
                <div key={index} className="relative">
                  {previewUrls[index] ? (
                    <div className="relative bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden group hover:shadow-md transition-all duration-200">
                      {/* Image Container */}
                      <div 
                        className="relative bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setIndexViewUrl(previewUrls[index] as string);
                          setIndexViewIndex(index);
                          setShowIndexViewModal(true);
                        }}
                      >
                        <div className="aspect-[3/4] flex items-center justify-center p-2">
                          <img
                            src={previewUrls[index] as string}
                            alt={`Index Page ${index + 1}`}
                            className="max-w-full max-h-full w-auto h-auto object-contain rounded-sm"
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                        {/* Overlay on hover */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-200 flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <div className="bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-lg">
                              <span className="text-xs font-medium text-gray-700 flex items-center gap-1.5">
                                <Image className="w-3.5 h-3.5" />
                                Click to preview
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Info Bar */}
                      <div className="p-3 bg-white border-t border-gray-100">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-xs font-semibold text-gray-900">
                            Page {index + 1}
                          </span>
                          {indexPagesImages[index]?.width > 0 && indexPagesImages[index]?.height > 0 && (
                            <span className="text-xs text-gray-500 font-mono">
                              {indexPagesImages[index].width}×{indexPagesImages[index].height}
                            </span>
                          )}
                        </div>
                        
                        {/* Action Buttons */}
                        {!readOnly && (
                          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const fileInput = document.getElementById(`index-upload-${index}`) as HTMLInputElement | null;
                                if (fileInput) {
                                  fileInput.click();
                                }
                              }}
                              className="flex-1 btn btn-xs btn-secondary text-xs py-1.5"
                              title="Replace image"
                            >
                              <Upload className="w-3 h-3 mr-1" />
                              Replace
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeIndexImage(index);
                              }}
                              className="btn btn-xs btn-error text-xs py-1.5 px-2"
                              title="Remove image"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                      
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleIndexImageSelect(e, index)}
                        className="hidden"
                        id={`index-upload-${index}`}
                        disabled={readOnly}
                      />
                    </div>
                  ) : (
                    <label
                      htmlFor={`index-upload-${index}`}
                      className={`relative border-2 border-dashed rounded-lg p-6 text-center flex flex-col items-center justify-center cursor-pointer transition-all duration-200 min-h-[200px] ${
                        indexUploading[index]
                          ? "border-gray-300 bg-gray-50 text-gray-400 pointer-events-none"
                          : "border-gray-300 bg-gray-50/50 text-gray-600 hover:border-primary-400 hover:bg-primary-50/30 hover:text-primary-700"
                      }`}
                    >
                      <div className={`mb-3 transition-transform duration-200 ${indexUploading[index] ? '' : 'group-hover:scale-110'}`}>
                        <Image className={`w-10 h-10 ${indexUploading[index] ? 'text-gray-400' : 'text-gray-400 group-hover:text-primary-500'}`} />
                      </div>
                      <span className="text-sm font-medium mb-1">
                        {indexUploading[index] ? (
                          <span className="flex items-center gap-2">
                            <span className="animate-spin rounded-full h-3 w-3 border-2 border-gray-400 border-t-transparent"></span>
                            Uploading...
                          </span>
                        ) : (
                          `Index Page ${index + 1}`
                        )}
                      </span>
                      <span className="text-xs text-gray-500 mt-1">
                        {indexUploading[index] ? 'Please wait...' : 'Click to upload'}
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleIndexImageSelect(e, index)}
                        className="hidden"
                        id={`index-upload-${index}`}
                        disabled={readOnly}
                      />
                    </label>
                  )}
                </div>
              ))}
            </div>
            {/* Index image preview modal (confirm-before-upload) */}
            <ImagePreviewModal
              isOpen={showIndexPreviewModal}
              imageUrl={indexPreviewUrl}
              fileName={indexPreviewFile?.name || ""}
              onConfirm={confirmIndexPreviewUpload}
              onCancel={cancelIndexPreviewUpload}
              isUploading={
                typeof indexPreviewIndex === "number"
                  ? !!indexUploading[indexPreviewIndex]
                  : false
              }
              title="Preview Index Image"
            />
            {/* Index image view modal (view uploaded image) */}
            {showIndexViewModal && indexViewUrl && typeof indexViewIndex === 'number' && (
              <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowIndexViewModal(false)}>
                <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                        <Image className="w-5 h-5 text-primary-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Index Page {indexViewIndex + 1}</h3>
                        {indexPagesImages[indexViewIndex]?.width > 0 && indexPagesImages[indexViewIndex]?.height > 0 && (
                          <p className="text-sm text-gray-500">
                            {indexPagesImages[indexViewIndex].width} × {indexPagesImages[indexViewIndex].height}px
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setShowIndexViewModal(false)}
                      className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Close"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="flex-1 p-6 overflow-auto bg-gray-50 flex items-center justify-center">
                    <img
                      src={indexViewUrl}
                      alt={`Index Page ${indexViewIndex + 1}`}
                      className="max-w-full max-h-[calc(90vh-120px)] object-contain rounded-lg shadow-lg"
                    />
                  </div>
                  {!readOnly && (
                    <div className="p-4 border-t border-gray-200 bg-white flex items-center justify-end gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setShowIndexViewModal(false);
                          const fileInput = document.getElementById(`index-upload-${indexViewIndex}`) as HTMLInputElement | null;
                          if (fileInput) {
                            fileInput.click();
                          }
                        }}
                        className="btn btn-secondary"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Replace Image
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowIndexViewModal(false);
                          removeIndexImage(indexViewIndex);
                        }}
                        className="btn btn-error"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Cover image preview modal (confirm-before-upload) */}
            <ImagePreviewModal
              isOpen={showCoverPreviewModal}
              imageUrl={coverPreviewUrl}
              fileName={coverPreviewFile?.name || ""}
              onConfirm={confirmCoverPreviewUpload}
              onCancel={cancelCoverPreviewUpload}
              isUploading={coverUploading}
              title="Preview Cover Image"
            />
            {/* PDF preview modal (confirm-before-upload) */}
            <PDFViewModal
              isOpen={showPdfPreviewModal}
              pdfUrl={pdfPreviewUrl}
              fileName={pdfPreviewFile?.name || ""}
              onConfirm={confirmPdfPreviewUpload}
              onCancel={cancelPdfPreviewUpload}
              isUploading={pdfUploading}
              title="Preview PDF Document"
            />
            {isSubmitted && errors.indexImages && (
              <p className="mt-2 text-sm text-error-600">
                {String(
                  errors.indexImages?.message ||
                    "At least 1 index image is required"
                )}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Settings */}
      {/* <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">Settings</h3>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPublished"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={!!watch("isPublished")}
                onChange={(e) => setValue("isPublished", e.target.checked)}
                disabled={readOnly}
              />
              <label
                htmlFor="isPublished"
                className="ml-2 text-sm text-gray-700"
              >
                Publish immediately
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isFeatured"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={!!watch("isFeatured")}
                onChange={(e) => setValue("isFeatured", e.target.checked)}
                disabled={readOnly}
              />
              <label
                htmlFor="isFeatured"
                className="ml-2 text-sm text-gray-700"
              >
                Featured content
              </label>
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPremium"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={!!watch("isPremium")}
                onChange={(e) => setValue("isPremium", e.target.checked)}
                disabled={readOnly}
              />
              <label htmlFor="isPremium" className="ml-2 text-sm text-gray-700">
                Premium content
              </label>
            </div>
          </div>
        </div>
      </div> */}

      {/* Actions */}
      {!readOnly && (
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={async () => {
              // show confirmation if there's an unsaved draft or changes
              if (!ebook && draftId && isAnythingChanged) {
                const confirmed = window.confirm(
                  "Are you sure you want to discard this draft? All data and uploads will be deleted."
                );
                if (!confirmed) return;
                try {
                  await ebookService.deleteDraft(draftId);
                } catch (e) {
                  // ignore cleanup errors
                }
                onCancel();
                return;
              }
              // fallback: just cancel
              onCancel();
            }}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button type="submit" disabled={loading} className="btn btn-primary">
            {loading ? "Saving..." : ebook ? "Update E-book" : "Create E-book"}
          </button>
        </div>
      )}
    </form>
  );
};

export default EBookForm;
